import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Button,
  Alert,
} from '@mui/material';
import { AddIcon, DeleteIcon } from './icons';
import { supabase } from '../lib/supabase';
import { Rule } from './AutoTopUpConfigModal'; // Reuse the Rule type
import { filterNonExpiryProducts } from '../utils/formatters';

type Product = {
  id: string;
  name: string;
  cost: number;
  category: string;
};

type RuleEditorProps = {
  rules: Rule[];
  onRulesChange: (newRules: Rule[]) => void;
};

export function RuleEditor({ rules, onRulesChange }: RuleEditorProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true);

      if (fetchError) throw fetchError;
      setProducts(data || []);
    } catch (err: any) {
      console.error('Error fetching products for RuleEditor:', err);
      setError(err.message || 'Failed to load top-up products.');
    }
  };

  const handleAddRule = () => {
    onRulesChange([
      ...rules,
      {
        type: 'airtime', 
        trigger: { threshold: 0 },
        action: { amount: 0 }, 
      },
    ]);
  };

  const handleRemoveRule = (index: number) => {
    onRulesChange(rules.filter((_, i) => i !== index));
  };

  const handleRuleChange = (index: number, field: string, value: any) => {
    const newRules = [...rules];
    const rule = newRules[index];

    if (field === 'type') {
      rule.type = value;
      if (value === 'airtime') {
        rule.action = { amount: 0 };
      } else {
        rule.action = { topup: '' };
      }
    } else if (field === 'threshold') {
      rule.trigger.threshold = Number(value) || 0;
    } else if (field === 'topup') {
      rule.action = { topup: String(value) };
    } else if (field === 'amount') {
      rule.action = { amount: Number(value) || 0 };
    }
    onRulesChange(newRules);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Typography variant="subtitle2" gutterBottom>
        Rules
      </Typography>
      {rules.map((rule, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex', 
            gap: 2, 
            alignItems: 'center', 
            p: 2, 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 1 
          }}
        >
          <TextField
            select
            label="Type"
            value={rule.type}
            onChange={(e) => handleRuleChange(index, 'type', e.target.value)}
            sx={{ minWidth: 120 }}
            size="small"
          >
            <MenuItem value="airtime">Airtime</MenuItem>
            <MenuItem value="data">Data</MenuItem>
          </TextField>

          <TextField
            type="number"
            label={`Threshold (${rule.type === 'airtime' ? 'R' : 'MB'})`}
            value={rule.trigger.threshold}
            onChange={(e) => handleRuleChange(index, 'threshold', e.target.value)}
            InputProps={{ inputProps: { min: 0 } }}
            sx={{ minWidth: 150 }}
            size="small"
          />
                    
          {rule.type === 'data' && (
            <TextField
              select
              label="Top-Up Product (Data)"
              value={rule.action.topup ?? ''}
              onChange={(e) => handleRuleChange(index, 'topup', e.target.value)}
              sx={{ flexGrow: 1 }}
              SelectProps={{ displayEmpty: true }}
              size="small"
            >
              <MenuItem value="" disabled><em>Select Data Bundle...</em></MenuItem>
              {filterNonExpiryProducts(products?.filter(p => p.category === 'mobile_data')).map((product) => {
                const displayCost = product.cost?.toFixed(2) ?? 'N/A';
                const displayName = product.name;
                const label = `${displayName} (R${displayCost})`;
                return (
                  <MenuItem key={product.id} value={product.id}>
                    {label}
                  </MenuItem>
                );
              })}
            </TextField>
          )}

          {rule.type === 'airtime' && (
            <TextField
              type="number"
              label="Top-Up Amount (R)"
              value={rule.action.amount ?? ''}
              onChange={(e) => handleRuleChange(index, 'amount', e.target.value)}
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              sx={{ minWidth: 150, flexGrow: 1 }}
              size="small"
            />
          )}
                  
          <IconButton
            onClick={() => handleRemoveRule(index)}
            color="error"
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}

      <Button
        startIcon={<AddIcon />}
        onClick={handleAddRule}
        variant="outlined"
        size="small"
        sx={{ alignSelf: 'flex-start' }}
      >
        Add Rule
      </Button>
    </Box>
  );
} 